import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { CopyableCode } from '../../components/ui/CopyableCode';
import { ApiError } from '../../api/client';
import { Markdown } from '../../components/ui/Markdown';
import { num } from '../../format';
import { useAuth } from '../auth/AuthContext';
import { PrintDialog } from '../labels/PrintDialog';
import { useNavigation } from '../layout/navigation';
import { ComponentPurchases } from '../purchases/ComponentPurchases';
import { ComponentBarcodes } from '../scan/ComponentBarcodes';
import { SerialsPanel } from '../serials/SerialsPanel';
import { SubstitutesPanel } from './SubstitutesPanel';
import { StockPanel } from '../stock/StockPanel';
import { componentReservations } from '../stock/api';
import { PackagesPanel } from '../stock/PackagesPanel';
import { ComponentForm } from './ComponentForm';
import { ComponentImages } from './ComponentImages';
import { BomUsagePanel } from './BomUsagePanel';
import { ResistorMarking } from './ResistorMarking';
import { useLabel } from './labels';
import { useUnitName } from './useUnitName';
import { archiveComponent, getComponent, getType, restoreComponent } from './api';

interface Props {
  componentId: string;
  onArchived: () => void;
  onOpenExisting: (componentId: string) => void;
  /** Passed up so the results list knows whether switching would lose work. */
  onDirtyChange?: (dirty: boolean) => void;
}

export function ComponentPanel({
  componentId,
  onArchived,
  onOpenExisting,
  onDirtyChange,
}: Props) {
  const { t } = useTranslation();
  const { pick } = useLabel();
  const { can } = useAuth();
  const unitName = useUnitName();
  const navigation = useNavigation();
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<'view' | 'edit' | 'copy'>('view');
  const [searchParams] = useSearchParams();
  const [printing, setPrinting] = useState(false);

  const component = useQuery({
    queryKey: ['components', componentId],
    queryFn: () => getComponent(componentId),
  });

  const typeDetail = useQuery({
    queryKey: ['reference', 'type', component.data?.component_type_id],
    queryFn: () => getType(component.data!.component_type_id!),
    enabled: Boolean(component.data?.component_type_id),
  });

  const reservations = useQuery({
    queryKey: ['stock', 'components', componentId, 'reservations'],
    queryFn: () => componentReservations(componentId),
  });

  const archive = useMutation({
    mutationFn: () => archiveComponent(componentId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['components'] });
      onArchived();
    },
  });

  const restore = useMutation({
    mutationFn: () => restoreComponent(componentId),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['components'] }),
  });

  const loadFailure = component.isError ? (
    <div className="panel__error component__load-error" role="alert">
      <p>{t(component.error instanceof ApiError && component.error.status === 404
        ? 'components.cardNotFound' : component.error instanceof ApiError && component.error.status === 403
          ? 'components.cardForbidden' : 'components.cardLoadFailed')}</p>
      <button type="button" onClick={() => void component.refetch()} disabled={component.isFetching}>
        {t(component.isFetching ? 'common.loading' : 'components.retrySearch')}
      </button>
    </div>
  ) : null;
  if (component.isLoading) return <p className="panel__hint" role="status">{t('common.loading')}</p>;
  if (!component.data) return loadFailure ?? <p className="panel__error" role="alert">{t('components.errors.unknown')}</p>;

  const item = component.data;

  if (mode !== 'view') {
    return (
      <>
        <ComponentForm
          component={mode === 'edit' ? item : undefined}
          copyFrom={mode === 'copy' ? item : undefined}
          onSaved={(saved) => {
            setMode('view');
            void queryClient.invalidateQueries({ queryKey: ['components'] });
            if (saved.id !== componentId) onOpenExisting(saved.id);
          }}
          onCancel={() => setMode('view')}
          onOpenExisting={(id) => {
            setMode('view');
            onOpenExisting(id);
          }}
          onDirtyChange={onDirtyChange}
        />
        {/* Photographs stay on screen while editing. They were only reachable
            from the read-only card, so adding one meant saving the form first —
            and the moment you have the part in hand is the moment you photograph
            it. A copy gets its own, so the gallery is hidden there. */}
        {mode === 'edit' && <ComponentImages componentId={componentId} packageId={item.package_id} />}
      </>
    );
  }

  const specs = typeDetail.data?.attributes ?? [];
  const filled = specs.filter((spec) => {
    const value = item.attributes[spec.key];
    return value !== undefined && value !== null && value !== '';
  });

  return (
    <article className="panel">
      {loadFailure}
      <header className="panel__head">
        <div>
          <h2>{item.name}</h2>
          {/* Code and part number are both copied far more often than they are
              read, and side by side without separation they read as one long
              string — which is how the wrong part gets ordered. */}
          <p className="panel__sub">
            {/* An archived part looks exactly like a stocked one that happens to
                have run out. Said on the card, because every refusal to order or
                post it will otherwise look like a fault. */}
            {item.archived_at && <span className="badge badge--warn">{t('common.archived')}</span>}
            <CopyableCode
              value={item.internal_code}
              className="components__code"
              label={t('components.code')}
            />
            {item.mpn && <CopyableCode value={item.mpn} label={t('components.mpn')} />}
            {item.manufacturer_name && <span>{item.manufacturer_name}</span>}
          </p>
        </div>
        <div className="panel__actions">
          {can('label.print') && (
            <button type="button" onClick={() => setPrinting(true)}>
              {t('labels.print')}
            </button>
          )}
          {/* "Who changed this and to what" is asked of the record in front of
              you, not of a log screen you then have to filter by hand. */}
          {navigation.canOpen('history') && (
            <button
              type="button"
              onClick={() => navigation.open('history', item.id, 'components')}
            >
              {t('audit.history')}
            </button>
          )}
          {/* Undoing an archiving is only clearing a date — nothing was
              destroyed by it — so the way back sits next to the way out. */}
          {can('catalog.write') && item.archived_at && (
            <button type="button" onClick={() => restore.mutate()} disabled={restore.isPending}>
              {t('components.restore')}
            </button>
          )}
          {can('catalog.write') && !item.archived_at && (
            <>
              <button type="button" onClick={() => setMode('edit')}>
                {t('components.edit')}
              </button>
            {/* A shelf of resistors differs only in value. Retyping the type,
                package, tolerance and rating for each is how people end up not
                recording the parameters at all. */}
              <button type="button" onClick={() => setMode('copy')}>
                {t('components.duplicate')}
              </button>
              <button type="button" onClick={() => archive.mutate()}>
                {t('components.archive')}
              </button>
            </>
          )}
        </div>
      </header>

      {printing && (
        <PrintDialog
          entityKind="component"
          entityId={componentId}
          onClose={() => setPrinting(false)}
        />
      )}

      {/* Parameters left, photographs right. The parameters are a narrow column
          of short values by nature, and underneath them the photographs used to
          start a screenful down with the whole right-hand half of the card left
          blank — on a shelf label that blank half is the difference between
          seeing the part and scrolling for it. */}
      <div className="panel__overview">
        <div className="panel__overview-main">
          <dl className="panel__facts">
            {item.type_name && (
              <>
                <dt>{t('components.type')}</dt>
                <dd>{item.type_name}</dd>
              </>
            )}
            {item.value_display && (
              <>
                <dt>{t('components.value')}</dt>
                <dd>{item.value_display}</dd>
              </>
            )}
            {item.package_code && (
              <>
                <dt>{t('components.package')}</dt>
                <dd title={item.package_description ?? undefined}>
                  {item.package_datasheet_url ? <a href={item.package_datasheet_url} target="_blank" rel="noreferrer">{item.package_code}</a> : item.package_code}
                </dd>
              </>
            )}
            <dt>{t('components.onHand')}</dt>
            <dd>
              {num(item.qty_on_hand)} {unitName(item.unit_code)}
            </dd>
            <dt>{t('components.reserved')}</dt>
            <dd>{num(item.reserved_qty)} {unitName(item.unit_code)}</dd>
            <dt>{t('components.freeAfterReservations')}</dt>
            <dd>{num(item.free_qty)} {unitName(item.unit_code)}</dd>
          </dl>

          {(reservations.data?.length ?? 0) > 0 && (
            <section className="component__project-reservations">
              <h3>{t('components.reservedByProjects', { n: reservations.data?.length ?? 0 })}</h3>
              <ul className="project__items">
                {reservations.data?.map((row) => (
                  <li key={row.project_id}>
                    <button type="button" onClick={() => navigation.open('project', row.project_id)}>
                      {row.project_code} — {row.project_name}
                    </button>
                    <strong>{num(row.quantity)} {unitName(item.unit_code)}</strong>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* Only for resistances: the codes and the colour bands mean nothing
              on a capacitor, and showing them there would be an invitation to
              misread a part. */}
          {typeDetail.data?.value_kind === 'resistance' && (
            <ResistorMarking
              ohms={item.value_base === null ? null : Number(item.value_base)}
              tolerance={
                typeof item.attributes.tolerance === 'string' ? item.attributes.tolerance : null
              }
            />
          )}

          {filled.length > 0 && (
            <dl className="panel__facts">
              {filled.map((spec) => {
                const raw = item.attributes[spec.key];
                const option = spec.enum_values?.find((entry) => entry.value === raw);
                return (
                  <div key={spec.key} className="panel__fact">
                    <dt>{pick(spec.label_ru, spec.label_en)}</dt>
                    <dd>
                      {option ? pick(option.label_ru, option.label_en) : String(raw)}
                      {spec.unit && ` ${spec.unit}`}
                    </dd>
                  </div>
                );
              })}
            </dl>
          )}

          {item.datasheet_url && (
            <p>
              <a href={item.datasheet_url} target="_blank" rel="noreferrer">
                {t('components.datasheet')}
              </a>
            </p>
          )}
        </div>

        <ComponentImages componentId={item.id} packageId={item.package_id} />
      </div>

      {/* Formatted, because a description is where the pinout, the quirks and
          the "buy this one, not that one" end up, and that is a list with a bit
          of emphasis. Folded to a few lines so it cannot push the stock and the
          photographs off the screen. */}
      {item.description && <Markdown source={item.description} lines={3} />}

      {/* The next three arrive shut. They are what a card is consulted for
          occasionally rather than read on the way past, and open they made every
          card four screens long. Each carries its own count, so none of them has
          to be opened to find out that it is empty.

          These are the manufacturer's barcodes, as opposed to ours. Kept on the
          card rather than only on the scan screen so that a wrong binding can be
          found and undone by someone looking at the part, not only by someone
          who happens to scan the packet again. */}
      <ComponentBarcodes componentId={item.id} />

      {/* Only for parts that are tracked individually. On a reel of resistors an
          empty "units" panel would be noise on every card in the catalogue. */}
      {item.track_serials && <SerialsPanel componentId={item.id} focusId={searchParams.get('serial')} />}

      {/* Placed above the stock panel deliberately: the moment somebody reads
          "0 on hand" is the moment the answer "but these three will do" is worth
          having, and putting it below would mean scrolling past the bad news to
          find it. Shut like the others, but the count on its heading is the part
          that has to be seen — "Alternatives 3" directly above an empty shelf
          says everything the open list would have. */}
      <SubstitutesPanel componentId={item.id} onOpen={onOpenExisting} />

      <BomUsagePanel componentId={item.id} />

      <StockPanel componentId={item.id} componentName={item.name} unit={unitName(item.unit_code)} />

      <PackagesPanel componentId={item.id} focusId={searchParams.get('package')} />

      {/* Below the stock, because it answers the question the stock raises:
          "none left — when did I last order these, and from whom". Only for
          people who may see prices; for everyone else it is not rendered at
          all rather than rendered empty. */}
      {can('purchase.write') && <ComponentPurchases componentId={item.id} />}
    </article>
  );
}
